import { lazy, ComponentType } from 'react'

const CHUNK_RELOAD_KEY = 'chunk-reload-attempted'
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 1000

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('error loading dynamically imported module')
  )
}

function reloadOnceForChunkError(): never {
  try {
    if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
      window.location.reload()
    }
  } catch {
    window.location.reload()
  }
  throw new Error('Chunk load failed, reloading...')
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function importWithRetry<T>(
  factory: () => Promise<T>,
  retries = MAX_RETRIES
): Promise<T> {
  let lastError: unknown

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await factory()
    } catch (error) {
      lastError = error
      if (!isChunkLoadError(error) || attempt === retries - 1) {
        break
      }
      await delay(RETRY_DELAY_MS * (attempt + 1))
    }
  }

  if (isChunkLoadError(lastError)) {
    reloadOnceForChunkError()
  }

  throw lastError
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() => importWithRetry(factory))
}

export { isChunkLoadError, reloadOnceForChunkError, CHUNK_RELOAD_KEY }
